import {
  ArrowDown,
  ArrowUp,
  CirclePause,
  CirclePlay,
  Clipboard,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FilePlus,
  FolderInput,
  FolderOpen,
  FolderPlus,
  FolderUp,
  History,
  LibraryBig,
  Link2,
  Maximize2,
  MessageCircle,
  MessageSquare,
  MessageSquarePlus,
  MonitorUp,
  PanelLeftOpen,
  Pencil,
  Pin,
  Play,
  Plus,
  Route,
  Settings,
  Share2,
  SquareArrowOutUpRight,
  Star,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Undo2,
  Upload,
  Users,
  type LucideIcon,
} from "lucide-react";
import type {
  ActionLabelContext,
  ObjectActionDefinition,
  ObjectActionGroupKey,
  ObjectActionId,
} from "./types";

export const OBJECT_ACTION_GROUPS: Record<
  ObjectActionGroupKey,
  readonly ObjectActionId[]
> = {
  navigation: [
    "run-now",
    "open",
    "open-in-new-tab",
    "locate-on-canvas",
    "edit",
    "manage",
    "open-location",
  ],
  // Creation: things made from nothing here — a canvas, a chat, a blank
  // document, a folder. Uploads are ingestion and live with the file IO verbs.
  create: ["new-canvas", "new-chat", "new-document", "new-folder"],
  organization: [
    "rename",
    "move",
    "copy-to",
    "duplicate",
    "copy",
    "wrap-in-section",
    "add-to-my-skills",
    "set-auto",
    "set-manual",
    "pause",
    "resume",
  ],
  // Share / Publish / Copy URL form one subsection — Copy URL surfaces only
  // once a file is published, so it reads as part of the sharing cluster.
  // Distribution: give access, publish a link, or take a copy out — Download
  // rides with sharing rather than sitting alone as a one-item section.
  sharing: [
    "share",
    "publish",
    "copy-url",
    "download",
    // Expand rides beside Download rather than leading the navigation
    // cluster: a block opens in the detail pane exactly like a document, and
    // the lightbox is the extra thing it can do — a second way to view the
    // file, next to the other file verbs.
    "expand",
    "add-members",
    "edit-workspace",
  ],
  favorites: ["star", "unstar"],
  file: ["upload-files", "upload-folder", "unpublish", "version-history"],
  "canvas-order": ["bring-to-front", "send-to-back", "route-automatically"],
  collaboration: ["chat-about", "comment", "execute-action-item"],
  destructive: [
    "remove-from-canvas",
    "show-on-canvas",
    "delete",
    "delete-wrapper-only",
    "remove-node",
    "restore",
    // Bulk archive sits with the other destructive rows so menus group it
    // with Hide/Delete instead of giving it a lone trailing subsection.
    "bulk-delete",
  ],
  bulk: ["bulk-move"],
};

// "Deleting" is archival — everything restorable lands on the Archived page —
// so resources with an archive home say "Archive". Plain canvas nodes are
// genuinely destroyed (no restore), so they keep "Delete". A file *entity*
// node is the exception among nodes: deleting it archives the backing file
// record, so it says "Archive" like every other file surface.
// One mechanic, two words. Everything you keep to hand is a *favorite* —
// canvases, files, skills — except a chat, which you *pin*: "favourite this
// conversation" reads oddly where "pin it" is exactly what you mean. The
// stored tag never changes; only the verb the surface speaks does.
function isChat(ctx: ActionLabelContext): boolean {
  return ctx.kind.type === "chat";
}
function favoriteLabel(ctx: ActionLabelContext): string {
  return isChat(ctx) ? "Pin" : "Add to favorites";
}
function unfavoriteLabel(ctx: ActionLabelContext): string {
  return isChat(ctx) ? "Unpin" : "Remove from favorites";
}
function favoriteIcon(ctx: ActionLabelContext): LucideIcon {
  return isChat(ctx) ? Pin : Star;
}

function trashLabel(ctx: ActionLabelContext): string {
  // A reference isn't archived — deleting it removes it from its container
  // while the workspace file it points at stays untouched. Say so. Covers both
  // the file-record kind (rail/list rows) and the canvas-node kind (node
  // context menu + floating toolbar) wrapping a reference row.
  if (ctx.kind.type === "file-record" && ctx.kind.isReference) {
    return ctx.kind.class === "flow-session"
      ? "Remove from chat"
      : "Remove from canvas";
  }
  if (
    ctx.kind.type === "canvas-node" &&
    ctx.kind.entityBase === "file-record" &&
    ctx.kind.entityIsReference
  ) {
    return "Remove from canvas";
  }
  if (
    ctx.kind.type === "canvas" ||
    ctx.kind.type === "chat" ||
    ctx.kind.type === "skill" ||
    ctx.kind.type === "workspace" ||
    ctx.kind.type === "file-record" ||
    (ctx.kind.type === "canvas-node" && ctx.kind.entityBase === "file-record")
  ) {
    return "Archive";
  }
  return "Delete";
}

function publishLabel(ctx: ActionLabelContext): string {
  return ctx.kind.type === "skill" ? "Publish to organization" : "Publish";
}

function downloadLabel(ctx: ActionLabelContext): string {
  // Skills bundle multiple files, so the skill surfaces download a zip.
  return ctx.kind.type === "skill" ? "Download zip" : "Download";
}

export const OBJECT_ACTION_CATALOG: Record<
  ObjectActionId,
  ObjectActionDefinition
> = {
  open: {
    id: "open",
    label: "Open",
    // Folders open by navigating into them, not in the right pane — show the
    // folder glyph there.
    icon: (ctx: ActionLabelContext) =>
      ctx.kind.type === "file-record" && ctx.kind.isFolder
        ? FolderOpen
        : PanelLeftOpen,
    group: "navigation",
  },
  expand: {
    id: "expand",
    // A block's second view: full-size in the canvas lightbox. Opening it is
    // Open, like any document — this only ever adds.
    label: "Expand",
    icon: Maximize2,
    group: "sharing",
  },
  "open-in-new-tab": {
    id: "open-in-new-tab",
    label: "Open in new tab",
    icon: ExternalLink,
    group: "navigation",
  },
  "locate-on-canvas": {
    id: "locate-on-canvas",
    label: "Locate on canvas",
    icon: ExternalLink,
    group: "navigation",
  },
  edit: {
    id: "edit",
    label: "Edit",
    icon: Pencil,
    group: "navigation",
  },
  manage: {
    id: "manage",
    label: "Manage",
    icon: Settings,
    group: "navigation",
  },
  rename: {
    id: "rename",
    label: "Rename",
    icon: Pencil,
    group: "organization",
  },
  move: {
    id: "move",
    label: "Move to",
    icon: FolderInput,
    group: "organization",
  },
  "copy-to": {
    id: "copy-to",
    label: "Copy to",
    icon: FolderInput,
    group: "organization",
  },
  duplicate: {
    id: "duplicate",
    label: "Duplicate",
    icon: Copy,
    group: "organization",
    shortcut: "⌘D",
  },
  copy: {
    id: "copy",
    label: "Copy",
    icon: Clipboard,
    group: "organization",
    shortcut: "⌘C",
  },
  "new-folder": {
    id: "new-folder",
    label: "New folder",
    icon: FolderPlus,
    group: "create",
  },
  "new-document": {
    id: "new-document",
    label: "New document",
    icon: FilePlus,
    group: "create",
  },
  "new-canvas": {
    id: "new-canvas",
    label: "New canvas",
    icon: Plus,
    group: "create",
  },
  "new-chat": {
    id: "new-chat",
    label: "New chat",
    icon: MessageSquarePlus,
    group: "create",
  },
  "upload-files": {
    id: "upload-files",
    label: "Upload files",
    icon: Upload,
    group: "file",
  },
  "upload-folder": {
    id: "upload-folder",
    label: "Upload folder",
    icon: FolderUp,
    group: "file",
  },
  "in-workspace": {
    id: "in-workspace",
    // A marker, not a verb: renders disabled on workspace files (and
    // references) so the tier reads at a glance in every menu.
    label: "In the workspace",
    icon: LibraryBig,
    group: "organization",
  },
  "add-to-workspace": {
    id: "add-to-workspace",
    // A3: moves the file (same id) into the workspace tree and leaves a
    // reference behind, so it stays visible where it was.
    label: "Add to workspace",
    icon: LibraryBig,
    group: "organization",
  },
  "copy-url": {
    id: "copy-url",
    label: "Copy URL",
    icon: Link2,
    group: "sharing",
  },
  "wrap-in-section": {
    id: "wrap-in-section",
    label: "Wrap in section",
    icon: FolderInput,
    group: "organization",
  },
  // Hide is the non-destructive counterpart to delete: the node (and, for a
  // file entity, the file's canvas membership) stays intact — the canvas just
  // stops rendering it. The rail lists hidden nodes with "Show on canvas".
  "remove-from-canvas": {
    id: "remove-from-canvas",
    label: "Hide on canvas",
    icon: EyeOff,
    group: "destructive",
  },
  "show-on-canvas": {
    id: "show-on-canvas",
    label: "Show on canvas",
    icon: Eye,
    group: "destructive",
  },
  "add-to-my-skills": {
    id: "add-to-my-skills",
    label: "Add to my skills",
    icon: Plus,
    group: "organization",
  },
  "set-auto": {
    id: "set-auto",
    label: "Set to Auto",
    icon: ToggleRight,
    group: "organization",
  },
  "set-manual": {
    id: "set-manual",
    label: "Set to Manual",
    icon: ToggleLeft,
    group: "organization",
  },
  "run-now": {
    id: "run-now",
    label: "Run now",
    icon: CirclePlay,
    group: "navigation",
  },
  pause: {
    id: "pause",
    label: "Pause",
    icon: CirclePause,
    group: "organization",
  },
  resume: {
    id: "resume",
    label: "Resume",
    icon: Play,
    group: "organization",
  },
  "open-location": {
    id: "open-location",
    label: "Open location",
    icon: SquareArrowOutUpRight,
    group: "navigation",
  },
  share: {
    id: "share",
    label: "Share",
    icon: Share2,
    group: "sharing",
  },
  "add-members": {
    id: "add-members",
    label: "Add members",
    icon: Users,
    group: "sharing",
  },
  "edit-workspace": {
    id: "edit-workspace",
    label: "Edit workspace",
    icon: Pencil,
    group: "sharing",
  },
  star: {
    id: "star",
    label: favoriteLabel,
    icon: favoriteIcon,
    group: "favorites",
  },
  unstar: {
    id: "unstar",
    label: unfavoriteLabel,
    icon: favoriteIcon,
    group: "favorites",
  },
  download: {
    id: "download",
    label: downloadLabel,
    icon: Download,
    group: "sharing",
    /** Export formats live in fileRecordExportCatalog.ts; expanded via variantSource. */
  },
  publish: {
    id: "publish",
    label: publishLabel,
    icon: MonitorUp,
    group: "sharing",
  },
  unpublish: {
    id: "unpublish",
    label: "Unpublish",
    icon: Trash2,
    group: "file",
    destructive: true,
  },
  "version-history": {
    id: "version-history",
    label: "Version history",
    icon: History,
    group: "file",
  },
  "bring-to-front": {
    id: "bring-to-front",
    label: "Bring to front",
    icon: ArrowUp,
    group: "canvas-order",
    shortcut: "⌘]",
  },
  "send-to-back": {
    id: "send-to-back",
    label: "Send to back",
    icon: ArrowDown,
    group: "canvas-order",
    shortcut: "⌘[",
  },
  "route-automatically": {
    id: "route-automatically",
    label: "Route automatically",
    icon: Route,
    group: "canvas-order",
  },
  "chat-about": {
    id: "chat-about",
    label: "Chat about this",
    icon: MessageSquare,
    group: "collaboration",
  },
  comment: {
    id: "comment",
    label: "Comment",
    icon: MessageCircle,
    group: "collaboration",
  },
  "execute-action-item": {
    id: "execute-action-item",
    label: "Execute",
    icon: Play,
    group: "collaboration",
  },
  delete: {
    id: "delete",
    label: trashLabel,
    icon: Trash2,
    group: "destructive",
    destructive: true,
    trashPolicy: "confirm",
  },
  "delete-wrapper-only": {
    id: "delete-wrapper-only",
    label: "Delete wrapper only",
    icon: Trash2,
    group: "destructive",
    destructive: true,
  },
  // The node alone, no record call: the exit for an Unavailable file card,
  // whose record is already gone. Same words as a reference's delete, same
  // meaning — the node leaves, nothing is archived.
  "remove-node": {
    id: "remove-node",
    label: "Remove from canvas",
    icon: Trash2,
    group: "destructive",
    destructive: true,
  },
  restore: {
    id: "restore",
    label: "Restore",
    icon: Undo2,
    group: "destructive",
  },
  "bulk-move": {
    id: "bulk-move",
    label: "Move to",
    icon: FolderInput,
    group: "bulk",
  },
  "bulk-delete": {
    id: "bulk-delete",
    label: "Archive",
    icon: Trash2,
    group: "destructive",
    destructive: true,
  },
};

export function resolveActionLabel(
  definition: ObjectActionDefinition,
  ctx: ActionLabelContext,
): string {
  return typeof definition.label === "function"
    ? definition.label(ctx)
    : definition.label;
}

export function resolveActionIcon(
  definition: ObjectActionDefinition,
  ctx: ActionLabelContext,
): LucideIcon {
  const icon = definition.icon;
  // Lucide icons are forwardRef exotic components (objects carrying $$typeof),
  // so a plain function here can only be a context resolver.
  if (typeof icon === "function" && !("$$typeof" in icon)) {
    return (icon as (ctx: ActionLabelContext) => LucideIcon)(ctx);
  }
  return icon as LucideIcon;
}

export function getObjectActionDefinition(
  id: ObjectActionId,
): ObjectActionDefinition {
  return OBJECT_ACTION_CATALOG[id];
}
